<template>
  <EquipmentRow data-cy="motor-row-equipment-row">
    <template #left></template>

    <template #default="{ toggle }">
      <AddRemoveButtons
        :hide-add-below="true"
        :disable-remove="!selectedMotorId"
        data-cy="motor-row-add-remove-buttons"
        @remove="handleMotorRemove"
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            :rule-names="[
              'motor_power',
              'startup_voltage',
              'flow_velocity_around_motor_low',
              'flow_velocity_around_motor_high',
              'flow_velocity_around_motor_high_abrasive',
              'motor_load_ratio',
              'motor_under_load_ratio',
              'motor_skin_temp',
              'motor_size_top_casing',
              'motor_size_bottom_casing',
              'motor_size_bypass_tubing_top_casing',
              'motor_size_bypass_tubing_bottom_casing',
              'motor_stage_temp',
              'motor_lock_plate',
              'motor_obsolete'
            ]"
            data-cy="motor-row-equipment-panel"
            @toggle="toggle"
          >
            <template #header>Motor</template>
            <template v-if="selectedMotor" #trigger>
              {{ computeMotorLabel(selectedMotor) }}
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <div class="col-12 text-center">
        <MotorSearchView
          :is-admin="false"
          data-cy="motor-row-motor-search-view"
          @select="handleMotorSelect($event, toggle)"
        />
      </div>
    </template>
    <template #right>
      <div
        v-if="!stack"
        class="flex align-items-center"
        style="gap: 1em"
        data-cy="motor-row-right"
      >
        <ConverterInput
          label="Length"
          :model-value="selectedMotor?.length_inches || 0"
          :base-unit="LengthUnits.Inches"
          :target-unit="getLargeLengthUnit('Length.convert.Inches')"
          :model="LargeLengthUnits.options()"
          mode="convert"
          data-cy="motor-row-length-converter"
        />
        <ConverterInput
          label="Weight"
          :model-value="selectedMotor?.weight_pounds || 0"
          :base-unit="MassUnits.Pounds"
          :model="MassUnits.options()"
          mode="convert"
        />
        <div class="flex align-items-center" style="gap: 1em">
          <ConverterInput
            label="Rerate"
            :disabled="!selectedMotor?.allow_rerate"
            :model-value="motorRerateRatio"
            :base-unit="PercentageUnits.Ratio"
            :target-unit="PercentageUnits.Percentage"
            :model="PercentageUnits.options()"
            mode="write"
            :step="0.05"
            :max="1.5"
            :min="0.5"
            is-ratio
            :show-stepper="true"
            :rule-names="['motor_rerate_range']"
            data-cy="motor-row-rerate-converter"
            @update:model-value="handleRerateChange"
          />
        </div>
      </div>
    </template>
  </EquipmentRow>
  <!-- Length and Weight below EquipmentRow in compact mode -->
  <div v-if="stack" class="compact-motor-dimensions flex gap-4">
    <ConverterInput
      label="Length"
      :model-value="selectedMotor?.length_inches || 0"
      :base-unit="LengthUnits.Inches"
      :target-unit="getLargeLengthUnit('Length.convert.Inches')"
      :model="LargeLengthUnits.options()"
      mode="convert"
      data-cy="motor-row-length-converter"
      class="flex-1"
    />
    <ConverterInput
      label="Weight"
      :model-value="selectedMotor?.weight_pounds || 0"
      :base-unit="MassUnits.Pounds"
      :model="MassUnits.options()"
      mode="convert"
      class="flex-1"
    />
  </div>
  <!-- Rerate and Shroud Diameter exposed for positioning by parent in compact mode -->
  <div v-if="stack" class="compact-motor-rerate-shroud-wrapper flex gap-4">
    <div class="flex-1">
      <ConverterInput
        label="Rerate"
        :disabled="!selectedMotor?.allow_rerate"
        :model-value="motorRerateRatio"
        :base-unit="PercentageUnits.Ratio"
        :target-unit="PercentageUnits.Percentage"
        :model="PercentageUnits.options()"
        mode="write"
        :step="0.05"
        :max="1.5"
        :min="0.5"
        is-ratio
        :show-stepper="true"
        :rule-names="['motor_rerate_range']"
        data-cy="motor-row-rerate-converter"
        @update:model-value="handleRerateChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ConverterInput from "@/components/ConverterInput.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import MotorSearchView from "./MotorSearchView.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { PercentageUnits } from "@/libs/units/percentage";
import { injectStrict } from "@/utils/utils";
import { computed } from "vue";
import {
  equipmentFormKey,
  parametersFormKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { computeMotorLabel } from "@/models/equipment/motors/properties";
import type { Motors } from "gql/api";
import {
  getMotorFieldName,
  getMotorFieldValue
} from "@/composables/forms/stackAwareFields";
import type { StackLocation } from "@/components/equipment/d";

const props = defineProps<{
  stack?: StackLocation;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const parametersForm = injectStrict(parametersFormKey);
const parameters = computed(() => parametersForm.data.value);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

// Stack-aware motor selection
const selectedMotor = computed(() => {
  if (props.stack === "lower") {
    return equipment.value?.r_lower_motor;
  }
  return equipment.value?.r_motor;
});

const selectedMotorId = computed(() => {
  if (props.stack === "lower") {
    return equipment.value.lower_motor_id;
  }
  return equipment.value.motor_id;
});

// Stack-aware rerate ratio
const motorRerateRatio = computed(() => {
  return (
    (getMotorFieldValue(parameters.value, "motorRerateRatio", props.stack) as
      | number
      | undefined) ?? undefined
  );
});

function handleMotorSelect(motor: Motors, toggle: () => void) {
  toggle();
  if (props.stack === "lower") {
    equipmentForm.update({
      r_lower_motor: motor,
      lower_motor_id: motor?.id
    } as Partial<typeof equipmentForm.data.value>);
  } else {
    equipmentForm.update({
      r_motor: motor,
      motor_id: motor?.id
    });
  }
}

function handleMotorRemove() {
  if (props.stack === "lower") {
    equipmentForm.update({
      r_lower_motor: null,
      lower_motor_id: null
    } as Partial<typeof equipmentForm.data.value>);
  } else {
    equipmentForm.update({
      r_motor: null,
      motor_id: null
    });
  }
}

function handleRerateChange(value: number | null) {
  const fieldName = getMotorFieldName("motorRerateRatio", props.stack);
  parametersForm.update({
    [fieldName]: value
  } as Partial<typeof parametersForm.data.value>);
}
</script>
